import {Pen} from 'lucide-react';
import type {MouseEventHandler, ReactNode} from 'react';

import type {AvatarProps} from '@/components/ui/avatar';
import {Avatar, AvatarFallback, AvatarImage} from '@/components/ui/avatar';
import {IconButton} from '@/components/ui/icon-button';
import {cn} from '@/utils/ui';

import {pillVariants, verticalLabelVariants} from './avatar-label-variants';

export type AvatarLayout = 'horizontal' | 'vertical' | 'inline';

export type AvatarLabelProps = {
  src?: string;
  fallback: string;
  label?: ReactNode;
  color?: NonNullable<AvatarProps['color']>;
  size?: NonNullable<AvatarProps['size']>;
  layout?: AvatarLayout;
  handleEdit?: MouseEventHandler<HTMLButtonElement>;
  className?: string;
};

const fallbackTextColor: Record<string, string> = {
  primary: 'text-primary-800',
  secondary: 'text-secondary-700',
  tertiary: 'text-accent-800',
};

const editButtonVariant: Record<string, 'default' | 'secondary-filled'> = {
  primary: 'default',
  secondary: 'secondary-filled',
  tertiary: 'default',
};

const editButtonPosition: Record<string, string> = {
  small: 'bottom-0 right-0',
  default: 'bottom-0 right-0',
  large: 'bottom-0 right-0',
  xlarge: 'bottom-1 right-1',
};

export function AvatarWithLabel({
  src,
  fallback,
  label,
  color = 'primary',
  size = 'default',
  layout = 'horizontal',
  handleEdit,
  className,
}: AvatarLabelProps) {
  const circle = (
    <div className="relative shrink-0">
      <Avatar color={color} size={size}>
        {src && <AvatarImage src={src} alt={fallback} />}
        <AvatarFallback className={fallbackTextColor[color]}>
          {fallback}
        </AvatarFallback>
      </Avatar>
      {handleEdit && (
        <IconButton
          type="button"
          variant={editButtonVariant[color]}
          className={cn('absolute', editButtonPosition[size])}
          size="small"
          onClick={handleEdit}
        >
          <Pen />
        </IconButton>
      )}
    </div>
  );

  if (!label) {
    return <div className={className}>{circle}</div>;
  }

  if (layout === 'horizontal') {
    return (
      <div className={cn('isolate flex items-center', className)}>
        <div
          className={cn(
            'relative z-10 shrink-0',
            size === 'small' ? '-mr-[5px]' : '-mr-2'
          )}
        >
          {circle}
        </div>
        <div className={cn(pillVariants({color, size}))}>{label}</div>
      </div>
    );
  }

  if (layout === 'vertical') {
    return (
      <div className={cn('flex flex-col items-center gap-0.5', className)}>
        {circle}
        <div className={cn(verticalLabelVariants({color, size}))}>{label}</div>
      </div>
    );
  }

  // inline — no fill, horizontal gap
  return (
    <div className={cn('flex items-center gap-2', className)}>
      {circle}
      {label}
    </div>
  );
}
